<template>
    <Card :padding="0" custom-classes="tw:overflow-hidden">
        <!-- Fila principal del evento -->
        <div
            class="tw:flex tw:flex-col tw:gap-4 tw:p-4 tw:xl:flex-row tw:xl:items-center tw:xl:justify-between tw:sm:p-5"
            :data-cy="'evento-row-' + evento.id"
        >
            <!-- Info del evento -->
            <div class="tw:flex tw:min-w-0 tw:flex-col tw:gap-4 tw:sm:flex-row tw:sm:items-start">
                <!-- Cabecera del partido (equipos) o miniatura del evento. En
                     móvil el bloque ocupa todo el ancho; el cartel mantiene su
                     miniatura fija y el hueco "Sin imagen" se estira a lo ancho. -->
                <div class="tw:flex tw:w-full tw:shrink-0 tw:items-start tw:sm:w-auto">
                    <!-- Cabecera del partido: escudos en tiles + VS tipográfico.
                         Una sola escala compacta en todos los tamaños: en un
                         evento de partido este bloque es un identificador, no
                         el protagonista de la ficha, y con la escala grande
                         (columnas de 112 px y escudos de 56 px) se comía el
                         ancho que necesita el título del evento. -->
                    <div
                        v-if="tieneEquipos"
                        data-cy="evento-equipos"
                        class="tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-2 tw:rounded-2xl tw:border tw:border-slate-200 tw:bg-slate-50 tw:px-3 tw:py-2 tw:sm:w-auto tw:sm:justify-start"
                    >
                        <div class="tw:flex tw:w-24 tw:flex-col tw:items-center tw:gap-1">
                            <div class="tw:flex tw:h-12 tw:w-12 tw:items-center tw:justify-center tw:rounded-xl tw:border tw:border-slate-200 tw:bg-white tw:shadow-sm">
                                <img
                                    v-if="equipoLocal && equipoLocal.imagen"
                                    :src="equipoLocal.imagen"
                                    :alt="equipoLocal.nombre"
                                    class="tw:h-8 tw:w-8 tw:object-contain"
                                />
                                <i v-else class="fas fa-shield-alt tw:text-[20px] tw:text-slate-300" aria-hidden="true"></i>
                            </div>
                            <span class="tw:line-clamp-2 tw:text-center tw:text-[11px] tw:font-bold tw:leading-tight tw:text-slate-900">
                                {{ equipoLocal?.nombre || '—' }}
                            </span>
                            <span class="tw:text-[9px] tw:font-semibold tw:uppercase tw:tracking-[0.16em] tw:text-slate-400">Local</span>
                        </div>

                        <!-- Texto, centrado con el escudo (h-12), no con el bloque -->
                        <span class="tw:flex tw:h-12 tw:items-center tw:text-[11px] tw:font-black tw:tracking-wide tw:text-slate-900">VS</span>

                        <div class="tw:flex tw:w-24 tw:flex-col tw:items-center tw:gap-1">
                            <div class="tw:flex tw:h-12 tw:w-12 tw:items-center tw:justify-center tw:rounded-xl tw:border tw:border-slate-200 tw:bg-white tw:shadow-sm">
                                <img
                                    v-if="equipoVisitante && equipoVisitante.imagen"
                                    :src="equipoVisitante.imagen"
                                    :alt="equipoVisitante.nombre"
                                    class="tw:h-8 tw:w-8 tw:object-contain"
                                />
                                <i v-else class="fas fa-shield-alt tw:text-[20px] tw:text-slate-300" aria-hidden="true"></i>
                            </div>
                            <span class="tw:line-clamp-2 tw:text-center tw:text-[11px] tw:font-bold tw:leading-tight tw:text-slate-900">
                                {{ equipoVisitante?.nombre || '—' }}
                            </span>
                            <span class="tw:text-[9px] tw:font-semibold tw:uppercase tw:tracking-[0.16em] tw:text-slate-400">Visitante</span>
                        </div>
                    </div>

                    <!-- Miniatura: cartel del evento. Sin overlay de edición: el
                         modal de imágenes de la entrada (email) no cambia esta
                         imagen y el botón al pasar el ratón confundía.
                         En móvil el bloque ocupa todo el ancho y 96 px de alto,
                         con cartel o con el hueco "Sin imagen"; desde `sm`
                         recupera la miniatura fija. -->
                    <div
                        v-else
                        data-cy="evento-media"
                        class="tw:flex tw:h-24 tw:w-full tw:flex-col tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-lg tw:border tw:border-slate-200 tw:bg-slate-50 tw:text-center tw:sm:h-20 tw:sm:w-28"
                    >
                        <img
                            v-if="imagenEvento"
                            :src="imagenEvento"
                            :alt="evento.nombre_completo"
                            class="tw:h-full tw:w-full tw:object-cover"
                        />
                        <template v-else>
                            <i class="far fa-image tw:text-[26px] tw:text-slate-400" aria-hidden="true"></i>
                            <span class="tw:mt-0.5 tw:text-[10px] tw:font-medium tw:leading-tight tw:text-slate-400">Sin imagen</span>
                        </template>
                    </div>
                </div>

                <div class="tw:min-w-0 tw:space-y-2.5">
                    <!-- Metadatos del evento, SIEMPRE encima del título. En móvil
                         todo fluye en un único flex-wrap: el código y el badge
                         ocupan la primera línea y las pastillas rellenan lo que
                         queda antes de saltar a la segunda. El grupo de
                         pastillas se disuelve (`contents`) para que cada pastilla
                         sea una pieza más del flujo, no un bloque aparte que cae
                         entero a la línea siguiente. Desde `sm` recupera su caja
                         propia y se agrupa a la derecha de la identidad. Las
                         pastillas nunca comparten fila con el contador de
                         sesiones: ahí se leían como parte del recuento. -->
                    <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5 tw:sm:gap-2" data-cy="evento-meta">
                        <div class="tw:flex tw:shrink-0 tw:flex-wrap tw:items-center tw:gap-2">
                            <span
                                class="tw:group/code tw:inline-flex tw:items-center tw:rounded-full tw:border tw:border-slate-300 tw:bg-slate-100 tw:px-2 tw:py-0.5 tw:text-[11px] tw:font-bold tw:tracking-wide tw:text-slate-800 tw:transition-colors tw:duration-150 tw:group-hover/code:border-slate-400 tw:group-hover/code:bg-slate-200"
                                :data-cy="'codigo-evento-' + evento.id"
                                :title="'Código del evento: ' + evento.codigo"
                            >
                                <i class="fas fa-hashtag tw:text-slate-400 tw:inline-block tw:translate-y-[0.03em]" aria-hidden="true"></i><span>{{ evento.codigo }}</span>

                                <!-- Copiar el código. Al pasar el ratón por el código
                                     la pastilla se ensancha un poco y el icono entra
                                     con un pop (ancho + escala + opacidad). También se
                                     revela con foco de teclado: si no, quedaría
                                     inalcanzable para quien no usa ratón. -->
                                <button
                                    type="button"
                                    :data-cy="'copiar-codigo-' + evento.id"
                                    class="tw:ml-0 tw:inline-flex tw:w-0 tw:shrink-0 tw:scale-75 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-full tw:border-0 tw:bg-transparent tw:p-0 tw:text-[11px] tw:text-slate-400 tw:opacity-0 tw:transition-all tw:duration-150 tw:group-hover/code:ml-1.5 tw:group-hover/code:w-[14px] tw:group-hover/code:scale-100 tw:group-hover/code:opacity-100 tw:focus-visible:ml-1.5 tw:focus-visible:w-[14px] tw:focus-visible:scale-100 tw:focus-visible:opacity-100 tw:hover:text-slate-700"
                                    :title="copiadoCodigo ? 'Código copiado' : 'Copiar código del evento'"
                                    :aria-label="copiadoCodigo ? 'Código copiado' : 'Copiar código del evento'"
                                    @click="copiarCodigo"
                                >
                                    <i
                                        :class="copiadoCodigo ? 'fas fa-check tw:text-emerald-600' : 'fas fa-copy'"
                                        aria-hidden="true"
                                    ></i>
                                </button>
                            </span>
                            <EstadoEventoBadge :status="evento.status" />
                        </div>

                        <div
                            v-if="tienePastillas"
                            class="tw:contents tw:sm:flex tw:sm:flex-wrap tw:sm:items-center tw:sm:gap-1.5"
                            data-cy="evento-pastillas"
                        >
                            <Pill
                                v-if="evento.tiene_recinto"
                                variant="slate"
                                size="sm"
                                weight="medium"
                                icon="fas fa-map-marker-alt"
                                icon-class="tw:text-blue-600"
                                label="Con recinto"
                            />
                            <Pill
                                v-if="evento.compartido"
                                variant="slate"
                                size="sm"
                                weight="medium"
                                label="Compartido"
                            />
                            <Pill
                                v-if="evento.compra_privada"
                                variant="slate"
                                size="sm"
                                weight="semibold"
                                label="Compra privada"
                            />
                            <Pill
                                v-if="showEvo"
                                variant="cyan"
                                size="sm"
                                weight="semibold"
                                label="AvanzaEsp"
                                title="Partido vinculado a AvanzaEsp"
                            />
                        </div>
                    </div>

                    <h2 class="tw:truncate tw:text-base tw:font-bold tw:leading-tight tw:text-slate-900 tw:sm:text-lg">
                        {{ evento.nombre_completo }}
                    </h2>

                    <!-- Contexto del evento en su propia fila, debajo del título:
                         solo el recuento de sesiones. Las pastillas viven en la
                         fila de metadatos, encima del título. -->
                    <div
                        class="tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-1.5 tw:pt-0.5 tw:text-[12px] tw:text-slate-600"
                        data-cy="evento-sesiones-resumen"
                    >
                        <span class="tw:inline-flex tw:items-center tw:gap-1.5">
                            <i class="far fa-calendar-alt tw:text-blue-600 tw:inline-block tw:translate-y-[0.03em]" aria-hidden="true"></i>
                            <span class="tw:font-semibold tw:text-slate-800">{{ textoSesiones.total }} {{ textoSesiones.nombre }}</span>
                            <span class="tw:text-slate-500">{{ textoSesiones.estado }}</span>
                        </span>
                    </div>
                </div>
            </div>

            <!-- Acciones a nivel de evento. En móvil, una línea fina las separa
                 de los datos: sin ella los datos y la botonera se leen como un
                 único bloque apelotonado. Desde `sm` la línea desaparece. -->
            <div class="tw:flex tw:flex-col tw:gap-1.5 tw:self-stretch tw:border-t tw:border-slate-200 tw:pt-3 tw:sm:flex-row tw:sm:flex-wrap tw:sm:items-center tw:sm:self-start tw:sm:border-0 tw:sm:pt-0 tw:xl:self-center">
                <!-- Imágenes del email de la entrada (cabecera, publicidad y
                     patrocinadores). No es el cartel del evento: para eso se
                     edita el evento. El rótulo lo dice para no confundir. -->
                <Button
                    v-if="variant === 'actuales' && puedeEditar"
                    variant="default"
                    icon="far fa-images"
                    custom-class="tw:w-full tw:sm:w-auto tw:justify-center"
                    @click="$emit('cambiar-imagen', evento)"
                >
                    Imágenes email
                </Button>
                <Button
                    v-if="permisos['eventos.editar'] && (variant === 'pasados' || !evento.compartido)"
                    as="router-link"
                    variant="default"
                    icon="fas fa-users"
                    custom-class="tw:w-full tw:sm:w-auto tw:justify-center"
                    :to="{ name: 'back.events.compania', params: { evento_id: evento.id } }"
                >
                    Compañía
                </Button>
                <Button
                    v-if="permisos['eventos.informe']"
                    as="router-link"
                    variant="default"
                    icon="fas fa-chart-bar"
                    custom-class="tw:w-full tw:sm:w-auto tw:justify-center"
                    :to="{ name: 'back.events.informe', params: { id: evento.id } }"
                >
                    Informe
                </Button>
                <Button
                    v-if="!evento.compartido"
                    variant="default"
                    icon="far fa-calendar-alt"
                    custom-class="tw:w-full tw:sm:w-auto tw:justify-center"
                    :data-cy="'sesiones-evento-' + evento.id"
                    :aria-expanded="expanded ? 'true' : 'false'"
                    @click="$emit('toggle-sesiones', evento)"
                >
                    Sesiones
                </Button>

                <OptionsDropdownComponent
                    v-if="eventoOptions.length"
                    custom-class="tw:w-full tw:sm:w-auto tw:justify-center"
                    container-class="tw:w-full tw:sm:w-auto"
                    :data-cy="'evento-opciones-' + evento.id"
                    :options="eventoOptions"
                />
            </div>
        </div>

        <!-- Barra inferior: affordance de apertura/cierre del panel. El botón
             "Sesiones" de la fila de acciones sigue siendo el disparador
             principal; esta barra añade el chevron y la transición. -->
        <button
            v-if="!evento.compartido"
            type="button"
            class="tw:flex tw:w-full tw:items-center tw:justify-end tw:border-t tw:border-slate-200 tw:bg-slate-50 tw:px-4 tw:py-1.5 tw:text-slate-400 tw:transition-colors tw:hover:bg-slate-100 tw:hover:text-slate-600"
            :aria-expanded="expanded ? 'true' : 'false'"
            :aria-label="expanded ? 'Cerrar sesiones' : 'Abrir sesiones'"
            :data-cy="'sesiones-barra-' + evento.id"
            @click="$emit('toggle-sesiones', evento)"
        >
            <i
                class="fas fa-chevron-down tw:transition-transform tw:duration-500 tw:ease-in-out"
                :class="expanded ? 'tw:rotate-180' : ''"
                aria-hidden="true"
            ></i>
        </button>

        <!-- Panel de sesiones: altura intrínseca con grid (0fr ↔ 1fr). -->
        <div
            class="sesiones-panel-grid tw:min-h-0 tw:bg-slate-50"
            :class="{
                'sesiones-panel-grid--open': expanded,
                'sesiones-panel-grid--closing': closingPanel
            }"
            :aria-hidden="expanded ? 'false' : 'true'"
        >
            <div class="sesiones-panel-clip tw:bg-slate-50">
                <div
                    v-if="renderPanel"
                    class="tw:bg-slate-50 tw:p-4 tw:sm:p-5"
                >
                    <div class="sesiones-panel-content">
                        <!-- Cabecera del panel: buscador + crear sesión en la misma fila
                             (en móvil apilados y el botón a ancho completo). El botón
                             "Sesiones" de la fila de acciones ya identifica el panel, así
                             que no se repite el rótulo. Se oculta mientras carga para no
                             duplicar los controles: en ese momento los pinta el skeleton. -->
                        <div
                            v-if="!loadingSesiones && !abriendoPanel"
                            class="tw:flex tw:flex-col tw:gap-2 tw:pb-3 tw:sm:flex-row tw:sm:items-center"
                        >
                            <div class="tw:w-full tw:sm:w-56">
                                <SearchComponent
                                    v-model="busquedaSesiones"
                                    placeholder="Buscar sesión"
                                    hide-label
                                    :data-cy="'buscar-sesion-' + evento.id"
                                />
                            </div>
                            <Button
                                v-if="permisos['eventos.crear']"
                                variant="primary"
                                icon="fas fa-plus"
                                custom-class="tw:w-full tw:sm:w-auto tw:justify-center tw:sm:ml-auto"
                                :data-cy="'crear-sesion-evento-' + evento.id"
                                @click="$emit('crear-sesion', evento)"
                            >
                                Crear sesión
                            </Button>
                        </div>

                        <!-- Cargando la página de sesiones: mismo esqueleto que el panel
                 real (cabecera buscador + crear, filas de SesionEventoRow y
                 pie de paginación), para que el cambio no salte al cargar. -->
                        <div v-if="loadingSesiones || abriendoPanel" class="tw:flex tw:flex-col tw:gap-3" data-cy="sesiones-skeleton">
                            <!-- Cabecera: buscador + Crear sesión -->
                            <div class="tw:flex tw:flex-col tw:gap-2 tw:pb-3 tw:sm:flex-row tw:sm:items-center">
                                <SkeletonItem height="tw:h-9" customClass="tw:w-full tw:rounded tw:sm:w-56" />
                                <SkeletonItem height="tw:h-9" customClass="tw:w-full tw:rounded tw:sm:w-[136px] tw:sm:ml-auto" />
                            </div>

                            <!-- Filas de sesión -->
                            <div
                                v-for="n in skeletonSesiones"
                                :key="'sesion-sk-' + n"
                                class="tw:flex tw:flex-col tw:gap-3 tw:rounded-xl tw:border tw:border-slate-200 tw:bg-white tw:p-4 tw:shadow-sm"
                            >
                                <!-- Fecha + apertura + título de la sesión -->
                                <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:border-b tw:border-slate-100 tw:pb-2.5">
                                    <SkeletonItem height="tw:h-7" customClass="tw:w-28 tw:rounded-full" />
                                    <SkeletonItem height="tw:h-7" customClass="tw:w-32 tw:rounded-full" />
                                    <SkeletonItem height="tw:h-4" customClass="tw:w-px" />
                                    <SkeletonItem height="tw:h-4" customClass="tw:w-40 tw:rounded" />
                                </div>
                                <!-- Venta / Gestor butacas / Precios / Editar / Borrar / Opciones,
                     pegados al borde derecho desde sm, igual que la fila real -->
                                <div class="tw:flex tw:flex-col tw:gap-1.5 tw:sm:flex-row tw:sm:flex-wrap tw:sm:items-center tw:sm:justify-end">
                                    <SkeletonItem height="tw:h-9" customClass="tw:w-full tw:rounded-sm tw:sm:w-24" />
                                    <SkeletonItem height="tw:h-9" customClass="tw:w-full tw:rounded-sm tw:sm:w-36" />
                                    <SkeletonItem height="tw:h-9" customClass="tw:w-full tw:rounded-sm tw:sm:w-24" />
                                    <SkeletonItem height="tw:h-9" customClass="tw:w-full tw:rounded-sm tw:sm:w-24" />
                                    <SkeletonItem height="tw:h-9" customClass="tw:w-full tw:rounded-sm tw:sm:w-24" />
                                    <SkeletonItem height="tw:h-9" customClass="tw:w-full tw:rounded-sm tw:sm:w-28" />
                                </div>
                            </div>

                            <!-- Pie de paginación (TableFooter) -->
                            <div class="tw:flex tw:flex-col tw:gap-3 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:px-4 tw:py-3 tw:shadow-sm tw:md:flex-row tw:md:items-center tw:md:justify-between">
                                <SkeletonItem height="tw:h-8" customClass="tw:w-28 tw:rounded" />
                                <SkeletonItem height="tw:h-4" customClass="tw:w-52 tw:rounded" />
                                <SkeletonItem height="tw:h-8" customClass="tw:w-40 tw:rounded" />
                            </div>
                        </div>

                        <!-- Error al cargar las sesiones. NO se disfraza de listado vacío:
                 un 404/500/red es un estado distinto de "este evento no tiene
                 sesiones" y confundirlos lleva a crear sesiones duplicadas.
                 El mensaje vacío queda reservado para una carga correcta. -->
                        <div
                            v-else-if="errorSesiones || !cargadoSesiones"
                            data-cy="sesiones-error"
                            class="tw:flex tw:flex-col tw:items-start tw:gap-3 tw:rounded-xl tw:border tw:border-red-200 tw:bg-red-50 tw:p-4"
                        >
                            <p class="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-medium tw:text-red-700">
                                <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
                                <span>No se pudieron cargar las sesiones.</span>
                            </p>
                            <Button
                                variant="default"
                                icon="fas fa-rotate-right"
                                data-cy="sesiones-reintentar"
                                @click="fetchSesiones"
                            >
                                Reintentar
                            </Button>
                        </div>

                        <div v-else-if="totalSesionesFiltradas" data-cy="evento-sesiones" class="tw:flex tw:flex-col tw:gap-3">
                            <SesionEventoRow
                                v-for="sesion in sesionesPaginadas"
                                :key="sesion.id"
                                :evento="evento"
                                :sesion="sesion"
                                :permisos="permisos"
                                :is-taquillero-visitante="isTaquilleroVisitante"
                                :variant="variant"
                                @edit="$emit('editar-sesion', $event, evento)"
                                @delete="$emit('borrar-sesion', $event, evento)"
                                @copy-url="$emit('copy-url', $event, evento)"
                                @test-venta="$emit('test-venta', $event, evento)"
                                @descargar-plano="$emit('descargar-plano', $event, evento)"
                            />

                            <!-- Paginación de sesiones (pie de tabla reutilizable) -->
                            <TableFooter
                                :value="modelSesiones"
                                :meta="metaSesiones"
                                :data-cy="'sesiones-footer-' + evento.id"
                                @input="modelSesiones = $event"
                            />
                        </div>
                        <p v-else-if="busquedaSesiones" class="tw:py-2 tw:text-sm tw:text-slate-500">
                            No hay sesiones que coincidan con la búsqueda.
                        </p>
                        <p v-else class="tw:py-2 tw:text-sm tw:text-slate-500">
                            Este evento todavía no tiene sesiones.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </Card>
</template>

<script setup>
    import { computed, onUnmounted, ref, watch } from 'vue';

    import Card from '@/modules/admin/ui/Card.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import OptionsDropdownComponent from '@/modules/admin/ui/OptionsDropdownComponent.vue';
    import Pill from '@/modules/admin/ui/Pill.vue';
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue';
    import { SkeletonItem } from '@/modules/admin/ui/skeleton';
    import TableFooter from '@/modules/admin/ui/table/TableFooter.vue';
    import EstadoEventoBadge from '@/modules/admin/eventos/components/EstadoEventoBadge.vue';
    import SesionEventoRow from '@/modules/admin/eventos/components/SesionEventoRow.vue';
    import { useSessionsList } from '@/composables/admin/eventos/useSessionsList';
    import { useCopyToClipboard } from '@/composables/common/useCopyToClipboard';

    const props = defineProps({
        evento: {
            type: Object,
            required: true
        },
        permisos: {
            type: Object,
            default: () => ({})
        },
        teatro: {
            type: Object,
            default: () => ({})
        },
        isTaquilleroVisitante: {
            type: Boolean,
            default: false
        },
        expanded: {
            type: Boolean,
            default: false
        },
        /**
         * Which listado this card belongs to. Shared layout; the event-level
         * actions differ (pasados edits from the dropdown and adds
         * Acreditaciones, actuales shows Editar as a button).
         */
        variant: {
            type: String,
            default: 'actuales',
            validator: v => ['actuales', 'pasados'].includes(v)
        },
        /**
         * Contador que el listado incrementa tras crear/editar/borrar una
         * sesión. Como el `useSessionsList` de esta ficha solo pide al endpoint
         * al expandirse, y `useGetEvents` no fuerza el remount de la tarjeta,
         * este token es la señal explícita para re-consultar el desplegable.
         * Se ignora si la ficha no está expandida.
         */
        refreshToken: {
            type: Number,
            default: 0
        }
    });

    const emit = defineEmits([
        'cambiar-imagen',
        'toggle-sesiones',
        'crear-sesion',
        'editar-sesion',
        'borrar-sesion',
        'copy-url',
        'test-venta',
        'descargar-plano',
        'borrar-evento'
    ]);

    /**
     * Copiar el código del evento al portapapeles. El icono sólo se revela al
     * pasar el ratón por el código (o con foco de teclado), así que la fila no
     * gana ruido visual; al copiar se anuncia con un toast.
     */
    const { copied: copiadoCodigo, copy: copiarAlPortapapeles } = useCopyToClipboard({
        successMessage: 'Código copiado',
        errorMessage: 'No se pudo copiar el código'
    });
    const copiarCodigo = () => copiarAlPortapapeles(props.evento.codigo);

    const puedeEditar = computed(() =>
        (props.permisos['eventos.editar'] || props.permisos['eventos.crear']) && !props.evento.compartido
    );

    const showEvo = computed(() => props.evento.evo_match_id && props.teatro.evo_enabled);

    /**
     * ¿Hay alguna pastilla de atributos que pintar? Si no hay ninguna, la fila
     * entera se omite para no dejar un hueco vacío entre identidad y título.
     */
    const tienePastillas = computed(() => Boolean(
        props.evento.tiene_recinto ||
            props.evento.compartido ||
            props.evento.compra_privada ||
            showEvo.value
    ));

    /**
     * Concordancia del contador de sesiones: "1 Sesión configurada" frente a
     * "N Sesiones configuradas". En español el cero va en plural.
     */
    const textoSesiones = computed(() => {
        const total = Number(props.evento.numero_sesiones) || 0;

        return total === 1
            ? { total, nombre: 'Sesión', estado: 'configurada' }
            : { total, nombre: 'Sesiones', estado: 'configuradas' };
    });

    /**
     * Listado de sesiones del evento paginado y buscable en SERVIDOR. Se pide
     * al endpoint cuando la ficha se expande (y se recarga al buscar, cambiar
     * de página o de tamaño de página). El evento ya no embebe las sesiones.
     */
    const {
        search: busquedaSesiones,
        sesiones: sesionesPaginadas,
        loading: loadingSesiones,
        error: errorSesiones,
        cargado: cargadoSesiones,
        model: modelSesiones,
        meta: metaSesiones,
        totalFiltradas: totalSesionesFiltradas,
        fetch: fetchSesiones
    } = useSessionsList(() => props.evento.id, { perPage: 5 });

    // El panel se monta al primer despliegue y se mantiene durante el cierre
    // para que el grid tenga contenido que animar; después se desmonta.
    const renderPanel = ref(false);
    const closingPanel = ref(false);
    const abriendoPanel = ref(false);
    let closeTimer = null;
    let openTimer = null;

    // El skeleton refleja las sesiones reales (máx. 5 por página). Así el
    // panel no abre a una altura y luego pega un salto al llegar las filas.
    const skeletonSesiones = computed(() => {
        const total = Number(props.evento.numero_sesiones) || 0;
        return Math.min(Math.max(total, 1), 5);
    });

    // Lazy-once: la primera apertura pide las sesiones; las siguientes
    // reutilizan lo ya cargado. Un cambio de sesión (crear/editar/borrar)
    // marca la caché como sucia para refrescar en la próxima apertura.
    const sesionesDesactualizadas = ref(false);

    watch(() => props.expanded, (expanded, anterior) => {
        if (closeTimer) {
            clearTimeout(closeTimer);
            closeTimer = null;
        }
        if (openTimer) {
            clearTimeout(openTimer);
            openTimer = null;
        }
        abriendoPanel.value = false;

        if (expanded) {
            closingPanel.value = false;
            renderPanel.value = true;

            if (!cargadoSesiones.value || sesionesDesactualizadas.value) {
                // La marca de obsolescencia se consume SOLO cuando arranca la
                // petición: si el panel se cierra durante la espera de 400 ms,
                // el timer se cancela y la marca debe seguir viva para que la
                // próxima apertura refresque.
                if (anterior === undefined) {
                    sesionesDesactualizadas.value = false;
                    fetchSesiones();
                } else {
                    abriendoPanel.value = true;
                    openTimer = setTimeout(() => {
                        abriendoPanel.value = false;
                        sesionesDesactualizadas.value = false;
                        fetchSesiones();
                        openTimer = null;
                    }, 400);
                }
            }
        } else if (renderPanel.value) {
            closingPanel.value = true;
            closeTimer = setTimeout(() => {
                renderPanel.value = false;
                closingPanel.value = false;
                closeTimer = null;
            }, 340);
        }
    }, { immediate: true });

    // Refresco tras crear/editar/borrar una sesión desde el listado: si la
    // ficha sigue expandida, se vuelve a pedir el desplegable para reflejar
    // el cambio (el endpoint devuelve las fechas ya actualizadas). Si está
    // cerrada, se marca sucia para pedirla al reabrir.
    watch(() => props.refreshToken, () => {
        if (props.expanded) {
            if (openTimer) {
                clearTimeout(openTimer);
                openTimer = null;
            }
            abriendoPanel.value = false;
            sesionesDesactualizadas.value = false;
            fetchSesiones();
        } else {
            sesionesDesactualizadas.value = true;
        }
    });

    onUnmounted(() => {
        if (closeTimer) clearTimeout(closeTimer);
        if (openTimer) clearTimeout(openTimer);
    });

    /**
     * Event poster. The listing must expose it as `imagen` with the absolute
     * URL already resolved by the API (`Evento::getImage()`, which uses
     * `Storage::url()`), so the card never builds the storage host itself.
     *
     * `imagen_cabecera*` is deliberately NOT used: those fields are the
     * ticket-email banner, not the event artwork.
     */
    const imagenEvento = computed(() => props.evento.imagen || null);

    const equipoLocal = computed(() => props.evento.equipo_local || null);
    const equipoVisitante = computed(() => props.evento.equipo_visitante || null);

    /**
     * Sport events carry both teams; then the card shows the two shields
     * instead of the header image.
     */
    const tieneEquipos = computed(() => Boolean(equipoLocal.value || equipoVisitante.value));

    /**
     * Event-level actions collapsed into OPCIONES. Los permisos replican el
     * listado legacy; Acreditaciones y Solicitudes preventa se ofrecen en
     * ambos listados porque son acciones del evento, no de la sesión.
     */
    const eventoOptions = computed(() => {
        const permisos = props.permisos;
        const evento = props.evento;
        const enPasados = props.variant === 'pasados';
        const options = [];

        // En pasados, toda la gestión se agrupa en el desplegable (el listado
        // original solo gateaba por eventos.editar, sin taquilleros.gestion).
        if (enPasados) {
            if (!permisos['eventos.editar']) return options;

            options.push({
                to: { name: 'back.events.editar', params: { evento_id: evento.id } },
                icon: 'edit',
                label: 'Editar'
            });
            options.push({
                to: {
                    name: 'back.acreditaciones.index',
                    // El listado filtra por `?evento_id=` (query), no por
                    // params: la ruta no declara `:evento_id` y los params
                    // sueltos se pierden al resolver la navegación.
                    query: { evento_id: evento.id }
                },
                icon: 'id-card',
                label: 'Acreditaciones'
            });

            if (props.teatro.invitaciones_para_abonados) {
                options.push({
                    to: { name: 'back.events.invitaciones-abonados', params: { evento_id: evento.id } },
                    icon: 'id-badge',
                    label: 'Invitaciones abonados'
                });
            }

            if (permisos['taquilleros.gestion']) {
                options.push({
                    to: { name: 'back.events.fields', params: { evento_id: evento.id } },
                    icon: 'th-list',
                    label: 'Campos personalizados'
                });
            }

            if (evento.preventa_configurada) {
                options.push({
                    to: { name: 'back.events.preventa.index', params: { evento_id: evento.id } },
                    icon: 'hourglass',
                    label: 'Solicitudes preventa'
                });
            }

            options.push({
                type: 'action',
                icon: 'trash',
                label: 'Borrar',
                action: () => emit('borrar-evento', evento)
            });

            return options;
        }

        if (permisos['taquilleros.gestion'] && !permisos['eventos.editar']) {
            options.push({
                to: { name: 'back.events.fields', params: { evento_id: evento.id } },
                icon: 'th-list',
                label: 'Campos personalizados'
            });
        }

        if (permisos['eventos.editar'] && permisos['taquilleros.gestion']) {
            // Editar vuelve al desplegable (como en el listado legacy) con el
            // mismo gate que tenía el botón visible: ambos permisos y nunca
            // sobre un evento compartido.
            if (!evento.compartido) {
                options.push({
                    to: { name: 'back.events.editar', params: { evento_id: evento.id } },
                    icon: 'edit',
                    label: 'Editar'
                });
            }

            if (props.teatro.invitaciones_para_abonados) {
                options.push({
                    to: { name: 'back.events.invitaciones-abonados', params: { evento_id: evento.id } },
                    icon: 'id-badge',
                    label: 'Invitaciones abonados'
                });
            }

            options.push({
                to: { name: 'back.events.fields', params: { evento_id: evento.id } },
                icon: 'th-list',
                label: 'Campos personalizados'
            });

            if (!evento.compartido) {
                options.push({
                    to: {
                        name: 'back.acreditaciones.index',
                        // El listado filtra por `?evento_id=` (query), no por
                        // params: la ruta no declara `:evento_id`.
                        query: { evento_id: evento.id }
                    },
                    icon: 'id-card',
                    label: 'Acreditaciones'
                });
            }

            if (evento.preventa_configurada) {
                options.push({
                    to: { name: 'back.events.preventa.index', params: { evento_id: evento.id } },
                    icon: 'hourglass',
                    label: 'Solicitudes preventa'
                });
            }

            options.push({
                to: { name: 'back.events.puertas.index', params: { evento_id: evento.id } },
                icon: 'door-open',
                label: 'Puertas'
            });
            options.push({
                to: { name: 'back.events.descuentos.index', params: { evento_id: evento.id } },
                icon: 'tags',
                label: 'Descuentos'
            });
            options.push({
                type: 'action',
                icon: 'trash',
                label: 'Borrar',
                action: () => emit('borrar-evento', evento)
            });
        }

        return options;
    });
</script>

<style scoped>
    /*
     * Altura intrínseca: el grid pasa de 0fr a 1fr y el navegador anima la
     * altura real del contenido, sin medir con JS ni reiniciar la transición
     * cuando el skeleton se sustituye por las filas. `min-height: 0` en el
     * clip es lo que permite colapsar hasta cero.
     */
    .sesiones-panel-grid {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.32s cubic-bezier(0.22, 1, 0.36, 1);
    }

    .sesiones-panel-grid--open {
        grid-template-rows: 1fr;
    }

    .sesiones-panel-clip {
        min-height: 0;
        overflow: hidden;
    }

    /* El contenido se desvanece solo al cerrar; al abrir aparece de una. */
    .sesiones-panel-grid--closing .sesiones-panel-content {
        opacity: 0;
        transition: opacity 0.16s ease;
    }
</style>
